import {
    Box,
    Flex,
    HStack,
    VStack,
    Text,
    Icon,
    IconButton,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Tag,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Button,
    useToast,
} from '@chakra-ui/react';
import { useState, useMemo, useEffect, useCallback, useRef, useContext } from 'react';
import { Icon as YaxleIcon, IconButton as YaxleIconButton, Tooltip } from '../../ui';
import { useStickyTableCopyFront } from '../../hooks/useStickyTableCopyFront';
import { stickyTableScrollCopySx, STICKY_TABLE_HEADER_Z_INDEX } from '../../utils/tableCopySx';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
    TbMessage,
    TbFileExport,
    TbMessagePlus,
    TbChecks,
} from 'react-icons/tb';
import { FiShoppingCart } from 'react-icons/fi';
import { SelectionTab, SelectionPart, SelectionTabKey } from '../../hooks/useGetOrderDetail';
import { formatPrice, formatPercentage } from '../../functions';
import { TableSettings } from '../../hooks/useTableSettings';
import { getTableSettingsSx } from '../../hooks/useTableSettings';
import TableSettingsButton from '../TableSettingsButton';
import PartType from '../PartType';
import ListPrice from '../ListPrice';
import PartNumber from '../PartNumber';
import SortArrowIcon from '../SortArrowIcon';
import SelectionStatusIcon from '../SelectionStatusIcon';
import PartBayButton from '../PartBayButton';
import CollapsibleSearch from '../CollapsibleSearch';
import VendorSelector from '../VendorSelector';
import PartOptionsModal from '../../modals/PartOptionsModal';
import PartOptionNoteModal from '../../modals/PartOptionNoteModal';
import PlaceOrderConfirmationModal from '../../modals/PlaceOrderConfirmationModal';
import SubmitQuoteModal from '../../modals/SubmitQuoteModal';
import { useTogglePartExclusion } from '../../hooks/useTogglePartExclusion';
import { useMarkPartOrdered } from '../../hooks/useMarkPartOrdered';
import SelectionPartActionsMenu from './SelectionPartActionsMenu';
import { useExportSelections } from '../../hooks/useExportSelections';
import { useConfirmAllSelections } from '../../hooks/useConfirmAllSelections';
import SelectionChangeIndicators, {
    getActionColumnMinWidth,
    getActionColumnSlotCount,
    getMaxIndicatorSlotCount,
} from './SelectionChangeIndicators';
import SessionContext from '../../contexts/SessionContext';
import { relocateAutoExcludedDuplicates } from './relocateAutoExcludedDuplicates';

interface SelectionFullScreenProps {
    tabs: Array<SelectionTab>;
    orderId: string;
    vin: string;
    refetch: () => void | Promise<unknown>;
    isEvOrder: boolean;
    quotesOnly: boolean;
    potentialTotal: boolean;
    onClose: () => void;
    tableSettings: TableSettings;
    onUpdateTableSettings: (newSettings: Partial<TableSettings>) => void;
    drpJob?: boolean;
}

interface FlatPart extends SelectionPart {
    phase: string;
    phaseKey: SelectionTabKey;
}

type SortField = 'listPrice' | 'buyPrice' | 'grossMargin';
type SortDirection = 'asc' | 'desc';

interface SortState {
    field: SortField | null;
    direction: SortDirection;
}

const PHASE_COLORS: Record<string, string> = {
    pending: 'blue',
    'no-quoted': 'orange',
    ordered: 'green',
    all: 'gray',
};

const getPhaseLabel = (key: string, title: string): string => {
    return title;
};

const SelectionFullScreen = ({
    tabs,
    orderId,
    vin,
    refetch,
    isEvOrder,
    quotesOnly,
    potentialTotal,
    onClose,
    tableSettings,
    onUpdateTableSettings,
    drpJob = false,
}: SelectionFullScreenProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedVendors, setSelectedVendors] = useState<Array<string> | 'all'>('all');
    const [sortState, setSortState] = useState<SortState>({ field: null, direction: 'asc' });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPartId, setSelectedPartId] = useState<string | null>(null);
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedPartForNote, setSelectedPartForNote] = useState<FlatPart | null>(null);
    const [isSubmitQuoteModalOpen, setIsSubmitQuoteModalOpen] = useState(false);
    const toggleExclusion = useTogglePartExclusion();
    const markPartOrdered = useMarkPartOrdered();
    const exportSelections = useExportSelections();
    const confirmAllSelections = useConfirmAllSelections();
    const toast = useToast();
    const sessionContext = useContext(SessionContext);
    const allowPlaceOrder = (isEvOrder && sessionContext?.permissions?.includes('place-ev-order')) || sessionContext?.permissions?.includes('place-order');

    const tableSettingsSx = useMemo(() => getTableSettingsSx(tableSettings), [tableSettings]);

    const displayTabs = useMemo(
        () => relocateAutoExcludedDuplicates(tabs),
        [tabs],
    );

    // Flatten all parts from all tabs into one list with phase info
    // Skip the "all" tab since it duplicates parts from the other tabs
    const allParts: FlatPart[] = useMemo(() => {
        const flat: FlatPart[] = [];
        displayTabs.forEach((tab) => {
            if (tab.key === 'all') return;
            tab.parts.forEach((part) => {
                flat.push({
                    ...part,
                    phase: getPhaseLabel(tab.key, tab.title),
                    phaseKey: tab.key,
                });
            });
        });
        return flat;
    }, [displayTabs]);

    const vendorOptions = useMemo(() => {
        return Array.from(
            new Set(
                allParts
                    .map((part) => (part.vendor || '').trim())
                    .filter(Boolean)
            )
        ).sort((a, b) => a.localeCompare(b));
    }, [allParts]);

    const vendorFilteredParts = useMemo(() => {
        if (selectedVendors === 'all') return allParts;
        return allParts.filter((part) => {
            const vendorName = (part.vendor || '').trim();
            return selectedVendors.includes(vendorName);
        });
    }, [allParts, selectedVendors]);

    useEffect(() => {
        if (selectedVendors === 'all') return;
        const validSelections = selectedVendors.filter((vendor) => vendorOptions.includes(vendor));
        if (validSelections.length !== selectedVendors.length) {
            setSelectedVendors(validSelections);
        }
    }, [selectedVendors, vendorOptions]);

    // Filter by search
    const filteredParts = useMemo(() => {
        if (!searchQuery.trim()) return vendorFilteredParts;
        const q = searchQuery.toLowerCase().trim();
        return vendorFilteredParts.filter((part) => {
            return (
                part.name.toLowerCase().includes(q) ||
                (part.partNumber && part.partNumber.toLowerCase().includes(q)) ||
                (part.correctedPartNumber && part.correctedPartNumber.toLowerCase().includes(q)) ||
                (part.vendor && part.vendor.toLowerCase().includes(q)) ||
                (part.delivery && part.delivery.toLowerCase().includes(q)) ||
                (part.type && part.type.toLowerCase().includes(q)) ||
                part.phase.toLowerCase().includes(q) ||
                (part.notes && part.notes.toLowerCase().includes(q))
            );
        });
    }, [vendorFilteredParts, searchQuery]);

    // Sort
    const sortedParts = useMemo(() => {
        if (!sortState.field) return filteredParts;
        const partsToSort = [...filteredParts];
        partsToSort.sort((a, b) => {
            const getValue = (part: FlatPart) => {
                switch (sortState.field) {
                    case 'listPrice': return part.listPrice ?? null;
                    case 'buyPrice': return part.buyPrice ?? null;
                    case 'grossMargin': return part.grossMargin ?? null;
                    default: return null;
                }
            };
            const aValue = getValue(a);
            const bValue = getValue(b);
            if (aValue === null || aValue === undefined) {
                return bValue === null || bValue === undefined ? 0 : (sortState.direction === 'asc' ? 1 : -1);
            }
            if (bValue === null || bValue === undefined) {
                return sortState.direction === 'asc' ? -1 : 1;
            }
            if (aValue === bValue) return 0;
            return sortState.direction === 'asc' ? aValue - bValue : bValue - aValue;
        });
        return partsToSort;
    }, [filteredParts, sortState]);

    const maxIndicatorSlots = useMemo(
        () => getMaxIndicatorSlotCount(sortedParts),
        [sortedParts]
    );

    const actionColumnMinW = useMemo(
        () => getActionColumnMinWidth(getActionColumnSlotCount(sortedParts)),
        [sortedParts]
    );

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { scrollContainerProps: stickyCopyScrollProps } = useStickyTableCopyFront(
        scrollContainerRef,
        sortedParts.length,
    );

    const toggleSort = (field: SortField) => {
        setSortState((prev) => {
            if (prev.field !== field) return { field, direction: 'asc' };
            if (prev.direction === 'asc') return { field, direction: 'desc' };
            return { field: null, direction: 'asc' };
        });
    };

    const renderSortableHeader = (label: string, field: SortField) => {
        const isActive = sortState.field === field;
        const direction = isActive ? sortState.direction : 'none';
        return (
            <Flex
                as="button"
                type="button"
                align="center"
                justify="flex-end"
                gap="6px"
                w="100%"
                color={isActive ? '#005BC4' : 'gray.600'}
                fontSize="xs"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="0.05em"
                cursor="pointer"
                onClick={(event) => {
                    event.stopPropagation();
                    toggleSort(field);
                }}
            >
                <Text as="span">{label}</Text>
                <Box lineHeight={0} color={isActive ? '#005BC4' : 'gray.400'}>
                    <SortArrowIcon direction={direction} />
                </Box>
            </Flex>
        );
    };

    const handleRowClick = (partId: string, isAssembly: boolean) => {
        if (isAssembly) return;
        setSelectedPartId(partId);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedPartId(null);
    };

    const handlePlaceOrderClick = () => {
        setIsConfirmationOpen(true);
    };

    const handleExportSelections = async () => {
        try {
            await exportSelections.mutateAsync({ orderId });
            toast({
                title: 'Selections export queued!',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
        } catch {
            toast({
                title: 'Failed to export selections',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleConfirmAllSelections = async () => {
        try {
            await confirmAllSelections.mutateAsync({ orderId });
            toast({
                title: 'All selections confirmed',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
        } catch {
            toast({
                title: 'Failed to confirm selections',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleCloseConfirmation = () => {
        setIsConfirmationOpen(false);
    };

    const handleNoteClick = (part: FlatPart) => {
        setSelectedPartForNote(part);
        setIsNoteModalOpen(true);
    };

    const handleNoteModalClose = () => {
        setIsNoteModalOpen(false);
        setSelectedPartForNote(null);
    };

    const handleExcludeToggle = async (partId: string, isAssembly: boolean) => {
        toggleExclusion.mutate({ orderPartId: partId, orderId, origin: 'ordering', isAssembly });
    };

    const handleMarkOrderedToggle = (partId: string) => {
        markPartOrdered.mutate({ orderId, orderPartId: partId });
    };

    // Whether there are any pending parts (to show place order button)
    const hasPendingParts = displayTabs.some((tab) => tab.key === 'pending' && tab.parts.length > 0);
    const isQuoteWorkflow = quotesOnly || potentialTotal;

    // Close on Escape key
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
        /* Backdrop overlay */
        <Box
            className="partbay-fullscreen-backdrop"
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.500"
            zIndex={1000}
            display="flex"
            alignItems="stretch"
            justifyContent="center"
            pt={3}
            pb={3}
            px={3}
            transition="right 220ms ease"
            onClick={(e) => {
                // Close when clicking the backdrop (not the card itself)
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            {/* Card */}
            <Box
                bg="white"
                borderRadius="xl"
                display="flex"
                flexDirection="column"
                overflow="hidden"
                w="100%"
                boxShadow="none"
            >
                {/* Top toolbar */}
                <Flex
                    px={6}
                    py={3}
                    borderBottom="1px solid"
                    borderColor="gray.200"
                    align="center"
                    justify="space-between"
                    flexShrink={0}
                    bg="white"
                    borderTopRadius="xl"
                >
                    {/* Left: search bar */}
                    <HStack spacing={3} flex={1} maxW="820px">
                        <Box flex={1}>
                            <CollapsibleSearch
                                isCompact={false}
                                width="100%"
                                placeholder="Search parts, part numbers, vendors..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </Box>
                        <VendorSelector
                            vendors={vendorOptions}
                            selectedVendors={selectedVendors}
                            onVendorChange={setSelectedVendors}
                        />
                        {(searchQuery || selectedVendors !== 'all') && (
                            <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
                                {sortedParts.length} result{sortedParts.length !== 1 ? 's' : ''}
                            </Text>
                        )}
                    </HStack>

                    {/* Right: actions */}
                    <HStack spacing={2}>
                        {hasPendingParts && (
                            <Menu placement="bottom-end">
                                <MenuButton
                                    as={Button}
                                    size="sm"
                                    variant="outline"
                                    colorScheme="gray"
                                    bg="white"
                                    rightIcon={<ChevronDownIcon />}
                                    _hover={{ bg: 'gray.50' }}
                                    _active={{ bg: 'gray.100' }}
                                    _focus={{ boxShadow: 'none' }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {isQuoteWorkflow || allowPlaceOrder ? 'More Actions' : 'Actions'}
                                </MenuButton>
                                <MenuList py={1} minW="200px">
                                    {!isQuoteWorkflow && (
                                        <MenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleExportSelections();
                                            }}
                                            isDisabled={exportSelections.isPending}
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            color="gray.700"
                                            _hover={{ bg: '#E6F1FD', color: '#0071E3' }}
                                        >
                                            <HStack spacing={2}>
                                                <Icon as={TbFileExport as React.ElementType} boxSize={4} color="gray.500" />
                                                <Text>{exportSelections.isPending ? 'Exporting...' : 'Export Selections'}</Text>
                                            </HStack>
                                        </MenuItem>
                                    )}
                                    <MenuItem
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleConfirmAllSelections();
                                        }}
                                        isDisabled={confirmAllSelections.isPending}
                                        fontSize="sm"
                                        px={3}
                                        py={2}
                                        color="gray.700"
                                        _hover={{ bg: '#E6F1FD', color: '#0071E3' }}
                                    >
                                        <HStack spacing={2}>
                                            <Icon as={TbChecks as React.ElementType} boxSize={4} color="gray.500" />
                                            <Text>{confirmAllSelections.isPending ? 'Confirming...' : 'Confirm All Selections'}</Text>
                                        </HStack>
                                    </MenuItem>
                                    {!isEvOrder && sessionContext?.permissions?.includes('request-quotes') && (
                                        <MenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsSubmitQuoteModalOpen(true);
                                            }}
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            color="gray.700"
                                            _hover={{ bg: '#E6F1FD', color: '#0071E3' }}
                                        >
                                            <HStack spacing={2}>
                                                <Icon as={TbMessagePlus as React.ElementType} boxSize={4} color="gray.500" />
                                                <Text>Request More Quotes</Text>
                                            </HStack>
                                        </MenuItem>
                                    )}
                                    {isQuoteWorkflow && allowPlaceOrder && (
                                        <MenuItem
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handlePlaceOrderClick();
                                            }}
                                            isDisabled={isModalOpen || toggleExclusion.isPending}
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            color="gray.700"
                                            _hover={{ bg: '#E6F1FD', color: '#0071E3' }}
                                        >
                                            <HStack spacing={2}>
                                                <Icon as={FiShoppingCart as React.ElementType} boxSize={4} color="gray.500" />
                                                <Text>Place Order</Text>
                                            </HStack>
                                        </MenuItem>
                                    )}
                                </MenuList>
                            </Menu>
                        )}
                        {hasPendingParts && isQuoteWorkflow && (
                            <PartBayButton
                                variant="outline"
                                size="sm"
                                colorScheme="partbay-blue"
                                text="Export Selections"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void handleExportSelections();
                                }}
                                isLoading={exportSelections.isPending}
                                loadingText="Exporting..."
                            />
                        )}
                        {hasPendingParts && !isQuoteWorkflow && allowPlaceOrder && (
                            <PartBayButton
                                variant="solid"
                                size="sm"
                                onClick={handlePlaceOrderClick}
                                text="Place Order"
                                colorScheme="partbay-blue"
                                isDisabled={isModalOpen || toggleExclusion.isPending}
                            />
                        )}
                        <TableSettingsButton
                            settings={tableSettings}
                            onUpdateSettings={onUpdateTableSettings}
                        />
                        <Tooltip label="Exit full screen" placement="top">
                            <YaxleIconButton
                                aria-label="Exit full screen"
                                icon={<YaxleIcon name="collapse" size="sm" />}
                                size="toolbar"
                                variant="ghost"
                                colorScheme="gray"
                                color="fg.muted"
                                onClick={onClose}
                            />
                        </Tooltip>
                    </HStack>
                </Flex>

                {/* Table */}
                <Box
                    ref={scrollContainerRef}
                    {...stickyCopyScrollProps}
                    flex={1}
                    overflowY="auto"
                    overflowX="auto"
                    sx={{
                        ...tableSettingsSx,
                        ...stickyTableScrollCopySx,
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#a0aec0' },
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#cbd5e0 transparent',
                    }}
                >
                    <Table variant="simple" size="sm" w="100%" minW="1200px" sx={{ 'th:first-of-type, td:first-of-type': { pl: 6 }, 'th:last-of-type, td:last-of-type': { pr: 6 } }}>
                        <Thead position="sticky" top={0} zIndex={STICKY_TABLE_HEADER_Z_INDEX} bg="white">
                            <Tr bg="gray.50">
                                <Th w="50px" minW="50px" py={2}></Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="180px">
                                    PART NAME
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="140px">
                                    PART NUMBER
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="150px">
                                    VENDOR NAME
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="110px">
                                    DELIVERY TIME
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="80px" isNumeric>
                                    QUANTITY
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="110px" isNumeric>
                                    {renderSortableHeader('LIST PRICE', 'listPrice')}
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="110px" isNumeric>
                                    {renderSortableHeader('BUY PRICE', 'buyPrice')}
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="120px" isNumeric>
                                    {renderSortableHeader('GROSS MARGIN', 'grossMargin')}
                                </Th>
                                <Th color="gray.600" fontSize="xs" fontWeight="semibold" py={2} minW="100px">
                                    PHASE
                                </Th>
                                <Th py={2} minW={actionColumnMinW} w={actionColumnMinW}></Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {sortedParts.length === 0 ? (
                                <Tr>
                                    <Td colSpan={11} py={12} textAlign="center">
                                        <Text fontSize="lg" color="gray.600" fontWeight="medium">
                                            {searchQuery ? 'No parts match your search' : 'No parts in selection'}
                                        </Text>
                                    </Td>
                                </Tr>
                            ) : (
                                sortedParts.map((part) => (
                                    <Tr
                                        key={`${part.phaseKey}-${part.partId}`}
                                        _hover={{
                                            bg: part.selectionStatus === 'manual' ? 'yellow.100' : part.selectionStatus === 'excluded' ? 'red.100' : part.selectionStatus === 'flagged' ? 'orange.100' : part.selectionStatus === 'ordered' ? 'green.100' : 'gray.50',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => handleRowClick(part.partId, part.isAssembly)}
                                        bg={part.selectionStatus === 'manual' ? 'yellow.50' : part.selectionStatus === 'excluded' ? 'red.50' : part.selectionStatus === 'flagged' ? 'orange.50' : part.selectionStatus === 'ordered' ? 'green.50' : 'transparent'}
                                    >
                                        <Td py={4} w="50px" textAlign="center">
                                            <SelectionStatusIcon
                                                status={part.selectionStatus}
                                                flagReason={part.flagReason}
                                            />
                                        </Td>
                                        <Td py={4} maxW="240px">
                                            <VStack align="flex-start" spacing={1}>
                                                <Text color="gray.900" fontWeight="medium" fontSize="sm" whiteSpace="pre-line">
                                                    {part.name}
                                                </Text>
                                                {part.type ? <PartType partType={part.type} /> : null}
                                            </VStack>
                                        </Td>
                                        <Td py={4}>
                                            {!part.isAssembly ? (
                                                <PartNumber
                                                    partNumber={part.partNumber}
                                                    correctedPartNumber={part.correctedPartNumber}
                                                    vin={vin}

                                                />
                                            ) : (
                                                <Text color="gray.900" fontSize="sm">—</Text>
                                            )}
                                        </Td>
                                        <Td py={4}>
                                            <Text color="gray.900" fontSize="sm">{part.vendor || '-'}</Text>
                                        </Td>
                                        <Td py={4}>
                                            <Text color="gray.900" fontSize="sm">{part.delivery || '-'}</Text>
                                        </Td>
                                        <Td py={4} isNumeric>
                                            <Text color="gray.900" fontSize="sm">{part.quantity || 1}</Text>
                                        </Td>
                                        <Td py={4} isNumeric>
                                            <ListPrice
                                                listPriceAdjusted={part.listPriceAdjusted}
                                                listPrice={part.listPrice}
                                            />
                                        </Td>
                                        <Td py={4} isNumeric>
                                            <Text color="gray.900" fontSize="sm" fontWeight="medium">
                                                {part.buyPrice ? formatPrice(part.buyPrice) : '-'}
                                            </Text>
                                        </Td>
                                        <Td py={4} isNumeric>
                                            <Text color="gray.900" fontSize="sm">
                                                {formatPercentage(part.grossMargin)}
                                            </Text>
                                        </Td>
                                        <Td py={4}>
                                            <Tag
                                                size="sm"
                                                colorScheme={PHASE_COLORS[part.phaseKey] || 'gray'}
                                                variant="subtle"
                                                borderRadius="md"
                                                fontWeight="medium"
                                                fontSize="xs"
                                            >
                                                {part.phase}
                                            </Tag>
                                        </Td>
                                        <Td py={4} minW={actionColumnMinW} w={actionColumnMinW} onClick={(e) => e.stopPropagation()}>
                                            <VStack spacing={1} align="center">
                                                <HStack spacing={2} justify="flex-end" w="100%">
                                                    <SelectionChangeIndicators
                                                        indicators={part.changeIndicators}
                                                        reservedIndicatorSlots={maxIndicatorSlots}
                                                    />
                                                    <Box w={8} h={8} flexShrink={0}>
                                                        {sessionContext?.permissions?.includes('add-option-note') && (
                                                            <IconButton
                                                                aria-label="Add Note"
                                                                icon={<Icon as={TbMessage as React.ElementType} w={4} h={4} />}
                                                                size="sm"
                                                                variant="ghost"
                                                                flexShrink={0}
                                                                w={8}
                                                                h={8}
                                                                color={part.notes ? '#0071E3' : 'gray.400'}
                                                                bg={part.notes && part.phaseKey === 'pending' ? 'blue.50' : 'transparent'}
                                                                onClick={() => handleNoteClick(part)}
                                                                _hover={{
                                                                    bg: part.notes && part.phaseKey === 'pending' ? 'blue.100' : 'transparent',
                                                                    color: part.notes ? '#0071E3' : 'gray.700',
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box w={8} h={8} flexShrink={0}>
                                                        <SelectionPartActionsMenu
                                                            phaseKey={part.phaseKey}
                                                            part={part}
                                                            isEvOrder={isEvOrder}
                                                            onExclude={() => handleExcludeToggle(part.partId, part.isAssembly)}
                                                            onMarkOrdered={() => handleMarkOrderedToggle(part.partId)}
                                                        />
                                                    </Box>
                                                </HStack>
                                            </VStack>
                                        </Td>
                                    </Tr>
                                ))
                            )}
                        </Tbody>
                    </Table>
                </Box>

                {/* Modals */}
                {selectedPartId && (
                    <PartOptionsModal
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                        orderPartId={selectedPartId}
                        orderId={orderId}
                        source="order"
                        drpJob={drpJob}
                    />
                )}
                <PlaceOrderConfirmationModal
                    isOpen={isConfirmationOpen}
                    onClose={handleCloseConfirmation}
                    orderId={orderId}
                />
                {selectedPartForNote !== null && (
                    <PartOptionNoteModal
                        key={selectedPartForNote.partId}
                        isOpen={isNoteModalOpen}
                        onClose={handleNoteModalClose}
                        selectedOptionId={selectedPartForNote.selectedOptionId || ''}
                        orderId={orderId}
                        orderPartId={selectedPartForNote.partId}
                        notes={selectedPartForNote.notes || ''}
                        estimatingNote={selectedPartForNote.estimatingNotes || ''}
                        listPriceAdjusted={selectedPartForNote.listPriceAdjusted || false}
                        refetch={refetch}
                        isEditable={selectedPartForNote.phaseKey === 'pending' && !!sessionContext?.permissions?.includes('add-option-note')}
                        partType={selectedPartForNote.type}
                        isAssembly={selectedPartForNote.isAssembly}
                        drpJob={drpJob}
                    />
                )}
                <SubmitQuoteModal
                    isOpen={isSubmitQuoteModalOpen}
                    onClose={() => setIsSubmitQuoteModalOpen(false)}
                    orderId={orderId}
                    resubmit
                />
            </Box>
            {/* end Card */}
        </Box>
    );
};

export default SelectionFullScreen;
